import classNames from 'classnames';
import React from 'react';
import { renderIcon } from './colored_icon';
import styles from './input.css';
import { Locale } from './locales';
import { removeNewlines } from './textarea';
import { TextRegularLight, TextSmallLight } from './typography';

type FormInputProps = {
  value: string;
  lang?: Locale;
  onChange?(newValue: string): void;
  invalid?: boolean;
  type: React.HTMLInputTypeAttribute | 'textarea';
  iconStart?: React.ComponentType | React.ReactNode;
  buttonEnd?: React.ReactNode;
  chips?: React.ReactNode;
  size?: 'medium' | 'large';
  textSize?: 'medium' | 'large';
  variant?: 'regular' | 'darker' | 'borderless';

  // passthrough
  onKeyDown?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  pattern?: string;
};

export const FormInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement | null,
  FormInputProps
>(function _FormInput(
  {
    value,
    onChange,
    onKeyDown,
    onKeyUp,
    invalid,
    iconStart,
    buttonEnd,
    chips,
    size = 'medium',
    textSize,
    variant = 'regular',
    ...passthrough
  },
  propsRef,
) {
  const ref = React.useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof propsRef === 'function') propsRef(el);
    else if (propsRef != null) propsRef.current = el;
  }, [value]);

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.target instanceof HTMLTextAreaElement) {
        removeNewlines(e.target);
      }
      onChange?.(e.target.value);
    },
    [onChange],
  );
  const handleClickIconStart = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // select all and focus
    el.select();
    el.focus();
  }, []);
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.currentTarget.blur();
      }
      onKeyDown?.(e);
    },
    [onKeyDown],
  );
  const handleKeyUp = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.currentTarget.blur();
      }
      onKeyUp?.(e);
    },
    [onKeyUp],
  );

  const Text =
    (textSize || size) === 'large' ? TextRegularLight : TextSmallLight;

  return (
    <Text tag="span">
      <span
        className={classNames(
          styles.container,
          size === 'large' && styles.isLarge,
          styles['variant-' + variant],
          buttonEnd && styles.hasButtonEnd,
          value === '' && styles.isEmptyValue,
          invalid && styles.isInvalid,
        )}
      >
        {React.Children.count(chips) > 0 && (
          <span className={styles.chipsRow}>{chips}</span>
        )}
        <span className={styles.inputRow}>
          {iconStart && (
            <span className={styles.iconStart} onClick={handleClickIconStart}>
              {renderIcon(iconStart)}
            </span>
          )}
          {passthrough.type === 'textarea' ? (
            <span
              className={classNames(styles.input, styles.autosizeContainer)}
            >
              <span className={styles.autosizeText}>
                {value || passthrough.placeholder}
                {value.endsWith('\n') && ' '}
              </span>
              <textarea
                ref={ref as React.RefObject<HTMLTextAreaElement>}
                className={styles.autosizeTextarea}
                value={value}
                onChange={handleChange}
                readOnly={!onChange}
                cols={1}
                rows={1}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                {...passthrough}
              />
            </span>
          ) : (
            <input
              ref={ref as React.RefObject<HTMLInputElement>}
              className={styles.input}
              value={value}
              onChange={handleChange}
              readOnly={!onChange}
              onKeyDown={handleKeyDown}
              onKeyUp={handleKeyUp}
              {...passthrough}
            />
          )}
          {buttonEnd && <span className={styles.buttonEnd}>{buttonEnd}</span>}
        </span>
      </span>
    </Text>
  );
});
